import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/store/app/appShellSlice';
import { generateIdempotencyKey } from '@/services/utils/idempotency';

// Compras apuntan al micro de inventario si existe.
const BASE_URL = process.env.NEXT_PUBLIC_INVENTORY_API_URL ?? 'http://localhost:8082';

/* ============================================
 * Utilidades & Type Guards
 * ==========================================*/
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const getNum = (v: unknown, fallback = 0): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const getStr = (v: unknown, fallback = ''): string =>
  typeof v === 'string' ? v : String(v ?? fallback);

/* ============================================
 * Tipos base compartidos
 * ==========================================*/
export interface ApiResponseBase<T = unknown> {
  success: boolean;
  message: string;
  data: T;
}

export type SKUItem = {
  sku?: string;
  inventory_id?: number;
  qty: number;
  unit_cost: number;
  serialized?: boolean;
  track_serial?: boolean;
  track_batch?: boolean;
  track_expiry?: boolean;
  serial_code?: string | null;
  serial_codes?: string[];
  batch_code?: string;
  expiry_date?: string;
};

export type CreatePOInput = {
  supplier_id: number;
  branch_id?: number | null;
  ordered_at?: string;
  items: { inventory_id: number; qty: number; unit_cost: number }[];
};

export type CreatePOResult = {
  id: number;
  status?: string | null;
  supplier_id?: number | null;
  branch_id?: number | null;
  ordered_at?: string | null;
  items?: Array<{ inventory_id: number; qty: number; unit_cost: number }>;
};

export type CreateReceiptInput = {
  supplier_id: number;
  branch_id?: number | null;
  purchase_order_id?: number | null;
  reference?: string | null;
  received_at?: string;
  items: SKUItem[];
  idempotencyKey?: string;
};

export type PurchaseOrderDetail = {
  po_id: number;
  status?: string;
  supplier_id?: number;
  supplier_name?: string;
  branch_id?: number;
  branch_name?: string;
  product_count?: number | null;
  received_qty_total?: number | null;
  pending_qty_total?: number | null;
  pending_lines?: number | null;
  ordered_at?: string | null;
  created_at?: string | null;
  items?: Array<PurchaseOrderLineDetail>;
};

export type PurchaseOrderLineDetail = {
  inventory_id?: number;
  sku?: string;
  name?: string;
  ordered_qty: number;
  received_qty: number;
  pending_qty: number;
  unit_cost: number;
  track_serial?: boolean;
  track_batch?: boolean;
  track_expiry?: boolean;
};

export type ReceiptListItem = {
  receipt_id: number;
  reference?: string | null;
  received_at: string;
  supplier: { id: number; name: string };
  branch: { id: number; name: string };
  purchase_order?: {
    id: number;
    status?: string | null;
    ordered_qty?: number | null;
    received_qty?: number | null;
    pending_qty?: number | null;
    pending_lines?: number | null;
  } | null;
  primary_sku?: string | null;
  total_qty: number;
  total_lines: number;
};

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export type PagedReceipts = Paginated<ReceiptListItem>;

export type ReceiptDetailItem = SKUItem & {
  id?: number | null;
  name?: string | null;
  received_qty?: number | null;
  pending_qty?: number | null;
  corrected_qty?: number | null;
  net_qty?: number | null;
  serial_codes?: string[];
};

export type ReceiptDetail = {
  id: number;
  supplier_id: number;
  supplier_name?: string | null;
  supplier_code?: string | null;
  branch_id: number;
  branch_name?: string | null;
  purchase_order_id?: number | null;
  purchase_order_status?: string | null;
  status?: string | null;
  document_status?: string | null;
  total_qty?: number | null;
  corrected_qty?: number | null;
  net_total_qty?: number | null;
  correction_count?: number | null;
  total_lines?: number | null;
  idempotency_key?: string | null;
  created_by?: number | null;
  reference?: string | null;
  received_at: string;
  created_at?: string | null;
  items: ReceiptDetailItem[];
};

export type BulkReceiptPrintInput = {
  receipt_ids: Array<number | string>;
  template: 'a4' | 'thermal';
  printer?: string | null;
  options?: Record<string, unknown>;
};

export type POConsolidatedPrintInput = {
  template: 'a4' | 'thermal';
  printer?: string | null;
  options?: Record<string, unknown>;
};

export type POLabelPrintInput = {
  scope: 'serialized' | 'all';
  template: 'a4' | 'thermal';
  printer?: string | null;
  options?: Record<string, unknown>;
};

export type PrintRequestPayload = {
  type: 'po_labels' | 'po_consolidated' | 'receipt_labels' | 'receipt_doc';
  format: 'a4' | 'thermal' | 'zpl';
  mode?: 'all' | 'serialized';
  po_ids?: number[];
  receipt_ids?: number[];
  options?: Record<string, unknown>;
};

export type PrintResult =
  | {
      blob: Blob;
      contentType: string;
      filename?: string | null;
    }
  | {
      contentType?: string | null;
      data?: string;
      zpl?: string;
      tspl?: string;
      filename?: string | null;
      payload?: unknown;
    };

/* ============================================
 * Tipos específicos de /purchases/orders (list)
 * ==========================================*/
export type ListPOsArgs = void | {
  status?: string; // pending | partial | received | cancelled
  supplier_id?: number | string;
  limit?: number;
  offset?: number;
  pending_only?: boolean;
};

export type POListItem = {
  po_id: number;
  status: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  branch_id?: number | null;
  branch_name?: string | null;
  pending_lines: number | null;
  product_count?: number | null;
  pending_qty_total?: number | null;
  received_qty_total?: number | null;
};

export type ListPOsData = Paginated<POListItem>;

export type POStatusMutationResult = {
  id: number;
  status: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  branch_id: number | null;
  branch_name: string | null;
  product_count: number | null;
  received_qty_total: number | null;
  pending_qty_total: number | null;
  pending_lines: number | null;
};

/** Shapes crudos que puede devolver el backend */
type RawPO = Record<string, unknown>;
type RawListPOs = {
  items?: unknown;
  total?: unknown;
  page?: unknown;
  page_size?: unknown;
};
type RawApiResponse<T = unknown> = {
  success?: unknown;
  message?: unknown;
  data?: T;
};

type RawReceiptLine = Record<string, unknown>;
type RawReceipt = {
  id?: unknown;
  supplier_id?: unknown;
  supplier_name?: unknown;
  supplier_code?: unknown;
  branch_id?: unknown;
  branch_name?: unknown;
  purchase_order_id?: unknown;
  purchase_order_status?: unknown;
  status?: unknown;
  document_status?: unknown;
  ordered_qty?: unknown;
  po_received_qty?: unknown;
  pending_qty?: unknown;
  pending_lines?: unknown;
  total_qty?: unknown;
  corrected_qty?: unknown;
  net_total_qty?: unknown;
  correction_count?: unknown;
  total_lines?: unknown;
  first_sku?: unknown;
  reference?: unknown;
  idempotency_key?: unknown;
  created_by?: unknown;
  received_at?: unknown;
  created_at?: unknown;
  items?: unknown;
};
type RawReceiptList = {
  items?: unknown;
  total?: unknown;
  limit?: unknown;
  offset?: unknown;
};

/* ============================================
 * Tipos específicos de /purchases/receipts (list)
 * ==========================================*/
export type ListReceiptsArgs = void | {
  from?: string;
  to?: string;
  supplier_id?: number | string;
  branch_id?: number | string;
  purchase_order_id?: number | string;
  po_status?: string; // legado
  receipt_status?: string;
  search?: string;
  limit?: number;
  offset?: number;
};

export type ReceiptLineApiItem = {
  id?: number | null;
  inventory_id: number | null;
  qty: number | null;
  unit_cost: number | null;
  received_qty: number | null;
  pending_qty: number | null;
  corrected_qty?: number | null;
  net_qty?: number | null;
  sku: string | null;
  name: string | null;
  serial_codes?: string[];
  batch_code?: string | null;
  expiry_date?: string | null;
};

export type ReceiptListApiItem = {
  id: number;
  supplier_id: number | null;
  supplier_name: string | null;
  supplier_code?: string | null;
  branch_id: number | null;
  branch_name?: string | null;
  purchase_order_id: number | null;
  purchase_order_status: string | null;
  status: string | null;
  document_status?: string | null;
  ordered_qty: number | null;
  po_received_qty: number | null;
  pending_qty: number | null;
  pending_lines: number | null;
  total_qty: number | null;
  corrected_qty?: number | null;
  net_total_qty?: number | null;
  correction_count?: number | null;
  total_lines: number | null;
  first_sku: string | null;
  reference: string | null;
  idempotency_key?: string | null;
  created_by?: number | null;
  items: ReceiptLineApiItem[];
  received_at: string | null;
  created_at: string | null;
};

export type CorrectReceiptLineInput = {
  receipt_item_id: number;
  qty_delta: number;
  serial_codes?: string[];
};

export type CorrectReceiptInput = {
  id: number | string;
  reason_code: string;
  note?: string;
  lines: CorrectReceiptLineInput[];
  idempotencyKey?: string;
};

export type ReverseReceiptInput = {
  id: number | string;
  reason_code: string;
  note?: string;
  idempotencyKey?: string;
};

export type ReceiptCorrectionItem = {
  id: number;
  receipt_item_id: number;
  inventory_id: number;
  qty_delta: number;
  unit_cost: number;
  ledger_id?: number | null;
};

export type ReceiptCorrection = {
  id: number;
  purchase_receipt_id: number;
  branch_id: number;
  correction_type: 'correction' | 'reverse';
  reason_code: string;
  note?: string | null;
  idempotency_key?: string | null;
  created_by?: number | null;
  document_status: 'applied' | 'corrected' | 'reversed';
  net_total_qty: number;
  created_at: string;
  items: ReceiptCorrectionItem[];
};

/* ============================================
 * API
 * ==========================================*/
export const purchasesApi = createApi({
  reducerPath: 'purchasesApi',
  // Si tu dynamicBaseQueryWithLogger ya está tipado como BaseQueryFn, perfecto.
  // De lo contrario, puedes declararlo así:
  // baseQuery: dynamicBaseQueryWithLogger(BASE_URL) as BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['PO', 'Receipt'] as const,
  endpoints: (builder) => ({
    /* ---------- GET /purchase-orders (list) ---------- */
    listPOs: builder.query<ApiResponseBase<ListPOsData>, ListPOsArgs>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && 'status' in args && args?.status) params.set('status', args.status);
        if (args && 'supplier_id' in args && args?.supplier_id != null)
          params.set('supplier_id', String(args.supplier_id));
        if (args && 'pending_only' in args && args?.pending_only)
          params.set('pending_only', 'true');
        if (args && 'limit' in args && args?.limit != null) params.set('limit', String(args.limit));
        if (args && 'offset' in args && args?.offset != null)
          params.set('offset', String(args.offset));
        const q = params.toString();
        return { url: `/v1/purchase-orders${q ? `?${q}` : ''}`, method: 'GET' } as const;
      },
      transformResponse: (respUnknown: unknown, _meta, args): ApiResponseBase<ListPOsData> => {
        // Aceptamos:
        // 1) { success, message, data: { items } }
        // 2) { success, message, data: [...] }  (items directo)
        const resp = isRecord(respUnknown)
          ? (respUnknown as RawApiResponse<RawListPOs | unknown[]>)
          : {};
        const data = resp.data;

        let rawItems: unknown[] = [];
        let total = 0;

        if (isRecord(data)) {
          rawItems = asArray((data as RawListPOs).items);
          total = getNum((data as RawListPOs).total, rawItems.length);
        } else if (Array.isArray(data)) {
          rawItems = data;
          total = rawItems.length;
        }

        const items: POListItem[] = rawItems.map((it): POListItem => {
          const r = isRecord(it) ? it : {};
          const po_id = getNum(r.po_id ?? r.id, 0);
          const status = (typeof r.status === 'string' ? r.status : null) as string | null;
          const supplier_id =
            r.supplier_id != null ? getNum(r.supplier_id, null as unknown as number) : null;
          const supplier_name = typeof r.supplier_name === 'string' ? r.supplier_name : null;
          const branch_id =
            r.branch_id != null ? getNum(r.branch_id, null as unknown as number) : null;
          const branch_name = typeof r.branch_name === 'string' ? r.branch_name : null;
          const itemsList = Array.isArray((r as any).items) ? ((r as any).items as any[]) : [];
          const product_count = getNum(r.product_count, itemsList.length) || null;
          const pending_qty_total =
            getNum(
              r.pending_qty_total,
              itemsList.reduce((acc: number, itm: any) => acc + getNum(itm?.pending_qty, 0), 0),
            ) || null;
          const received_qty_total =
            getNum(
              r.received_qty_total,
              itemsList.reduce((acc: number, itm: any) => acc + getNum(itm?.received_qty, 0), 0),
            ) || null;
          const pendingLinesFromItems = itemsList.filter(
            (itm: any) => getNum(itm?.pending_qty, 0) > 0,
          ).length;
          const pending_lines =
            r.pending_lines != null
              ? getNum(r.pending_lines, null as unknown as number)
              : pendingLinesFromItems || null;

          return {
            po_id,
            status,
            supplier_id,
            supplier_name,
            branch_id,
            branch_name,
            pending_lines,
            product_count,
            pending_qty_total,
            received_qty_total,
          };
        });

        const message = getStr(resp.message, '');
        const success = Boolean(resp.success);

        return {
          success,
          message,
          data: {
            items,
            total,
            page: 1,
            page_size: items.length,
          },
        };
      },
      transformErrorResponse: handlerErrorResponse('Purchases.ListPOs'),
      providesTags: ['PO'],
    }),

    /* ---------- GET /purchase-receipts (list) ---------- */
    listReceipts: builder.query<
      { items: ReceiptListApiItem[]; total: number; limit: number; offset: number },
      ListReceiptsArgs
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && 'from' in args && args?.from) params.set('from', args.from);
        if (args && 'to' in args && args?.to) params.set('to', args.to);
        if (args && 'supplier_id' in args && args?.supplier_id != null)
          params.set('supplier_id', String(args.supplier_id));
        if (args && 'branch_id' in args && args?.branch_id != null)
          params.set('branch_id', String(args.branch_id));
        if (args && 'purchase_order_id' in args && args?.purchase_order_id != null)
          params.set('purchase_order_id', String(args.purchase_order_id));
        if (args && 'po_status' in args && args?.po_status) params.set('po_status', args.po_status);
        if (args && 'receipt_status' in args && args?.receipt_status)
          params.set('receipt_status', args.receipt_status);
        if (args && 'search' in args && args?.search) params.set('search', args.search);
        if (args && 'limit' in args && args?.limit != null) params.set('limit', String(args.limit));
        if (args && 'offset' in args && args?.offset != null)
          params.set('offset', String(args.offset));
        const q = params.toString();
        return { url: `/v1/purchase-receipts${q ? `?${q}` : ''}`, method: 'GET' } as const;
      },
      transformResponse: (respUnknown: unknown, _meta, args) => {
        const resp = isRecord(respUnknown) ? (respUnknown as RawApiResponse<RawReceiptList>) : {};
        const data = isRecord(resp.data) ? (resp.data as RawReceiptList) : {};

        const rawItems: RawReceipt[] = Array.isArray(data.items)
          ? (data.items as RawReceipt[])
          : [];

        const items: ReceiptListApiItem[] = rawItems.map((raw): ReceiptListApiItem => {
          const r = isRecord(raw) ? (raw as RawReceipt) : {};
          const rawLines: RawReceiptLine[] = Array.isArray(r.items)
            ? (r.items as RawReceiptLine[])
            : [];

          const itemsNormalized: ReceiptLineApiItem[] = rawLines.map((it) => {
            const line = isRecord(it) ? it : {};
            return {
              id: line.id != null ? getNum(line.id, 0) : null,
              inventory_id: line.inventory_id != null ? getNum(line.inventory_id, 0) : null,
              qty: line.qty != null ? getNum(line.qty, 0) : null,
              unit_cost: line.unit_cost != null ? getNum(line.unit_cost, 0) : null,
              received_qty: line.received_qty != null ? getNum(line.received_qty, 0) : null,
              pending_qty: line.pending_qty != null ? getNum(line.pending_qty, 0) : null,
              corrected_qty: line.corrected_qty != null ? getNum(line.corrected_qty, 0) : null,
              net_qty: line.net_qty != null ? getNum(line.net_qty, 0) : null,
              sku: typeof line.sku === 'string' ? line.sku : null,
              name: typeof line.name === 'string' ? line.name : null,
              serial_codes: Array.isArray(line.serial_codes)
                ? line.serial_codes.map((value) => getStr(value, '')).filter(Boolean)
                : [],
              batch_code: typeof line.batch_code === 'string' ? line.batch_code : null,
              expiry_date: typeof line.expiry_date === 'string' ? line.expiry_date : null,
            };
          });

          return {
            id: getNum(r.id ?? (r as Record<string, unknown>).receipt_id, 0),
            supplier_id: r.supplier_id != null ? getNum(r.supplier_id, 0) : null,
            supplier_name: typeof r.supplier_name === 'string' ? r.supplier_name : null,
            supplier_code: typeof r.supplier_code === 'string' ? r.supplier_code : null,
            branch_id: r.branch_id != null ? getNum(r.branch_id, 0) : null,
            branch_name: typeof r.branch_name === 'string' ? r.branch_name : null,
            purchase_order_id: r.purchase_order_id != null ? getNum(r.purchase_order_id, 0) : null,
            purchase_order_status:
              typeof r.purchase_order_status === 'string' ? r.purchase_order_status : null,
            status: typeof r.status === 'string' ? r.status : null,
            document_status: typeof r.document_status === 'string' ? r.document_status : null,
            ordered_qty: r.ordered_qty != null ? getNum(r.ordered_qty, 0) : null,
            po_received_qty: r.po_received_qty != null ? getNum(r.po_received_qty, 0) : null,
            pending_qty: r.pending_qty != null ? getNum(r.pending_qty, 0) : null,
            pending_lines: r.pending_lines != null ? getNum(r.pending_lines, 0) : null,
            total_qty: r.total_qty != null ? getNum(r.total_qty, 0) : null,
            corrected_qty: r.corrected_qty != null ? getNum(r.corrected_qty, 0) : null,
            net_total_qty: r.net_total_qty != null ? getNum(r.net_total_qty, 0) : null,
            correction_count: r.correction_count != null ? getNum(r.correction_count, 0) : null,
            total_lines: r.total_lines != null ? getNum(r.total_lines, 0) : null,
            first_sku: typeof r.first_sku === 'string' ? r.first_sku : null,
            reference: typeof r.reference === 'string' ? r.reference : null,
            idempotency_key: typeof r.idempotency_key === 'string' ? r.idempotency_key : null,
            created_by: r.created_by != null ? getNum(r.created_by, 0) : null,
            items: itemsNormalized,
            received_at: typeof r.received_at === 'string' ? r.received_at : null,
            created_at: typeof r.created_at === 'string' ? r.created_at : null,
          };
        });

        const fallbackLimit =
          args && typeof args === 'object' && 'limit' in args && args.limit != null
            ? Number(args.limit)
            : items.length;
        const fallbackOffset =
          args && typeof args === 'object' && 'offset' in args && args.offset != null
            ? Number(args.offset)
            : 0;

        return {
          items,
          limit: getNum(data.limit, fallbackLimit),
          offset: getNum(data.offset, fallbackOffset),
          total: getNum(data.total, items.length),
        };
      },
      transformErrorResponse: handlerErrorResponse('Purchases.ListReceipts'),
      providesTags: ['Receipt'],
    }),

    /* ---------- POST /purchases/orders ---------- */
    createPO: builder.mutation<CreatePOResult, CreatePOInput>({
      query: (body) => ({
        url: '/v1/purchase-orders',
        method: 'POST',
        body: {
          supplier_id: body.supplier_id,
          branch_id: body.branch_id,
          ordered_at: body.ordered_at,
          items: (body.items || []).map((it) => ({
            inventory_id: Number(it.inventory_id),
            qty: Number(it.qty),
            unit_cost: Number(it.unit_cost),
          })),
        },
      }),
      transformResponse: (resp: ApiResponseBase<any>) => {
        const data = isRecord(resp?.data) ? resp.data : {};
        const normalized: CreatePOResult = {
          id: getNum((data as any).id ?? (data as any).po_id, 0),
          status: getStr((data as any).status, '').trim() || null,
          supplier_id: getNum((data as any).supplier_id, 0) || null,
          branch_id: getNum((data as any).branch_id, 0) || null,
          ordered_at: getStr((data as any).ordered_at, '') || null,
          items: asArray((data as any).items || []).map((it: any) => ({
            inventory_id: getNum(it?.inventory_id, 0),
            qty: getNum(it?.qty, 0),
            unit_cost: getNum(it?.unit_cost, 0),
          })),
        };
        return normalized;
      },
      transformErrorResponse: handlerErrorResponse('Purchases.CreatePO'),
      invalidatesTags: ['PO'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              open: true,
              message: e?.message || 'Error al crear la orden de compra',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Purchases.CreatePO'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'purchases.po.create',
              endpointName: e?.endpointName || 'Purchases.CreatePO',
              validationErrors:
                (e as unknown as { details?: unknown })?.details ??
                (e as unknown as { originalError?: { errors?: unknown } })?.originalError?.errors ??
                {},
            }),
          );
        }
      },
    }),

    /* ---------- POST /purchases/receipts ---------- */
    createReceipt: builder.mutation<ApiResponseBase<unknown>, CreateReceiptInput>({
      query: (body) => {
        const items = (body.items || []).map((it) => {
          const payload: Record<string, unknown> = {
            inventory_id: it.inventory_id ?? null,
            qty: Number(it.serialized ? 1 : it.qty),
            unit_cost: Number(it.unit_cost),
          };
          if (it.sku) payload.sku = it.sku;
          if (Array.isArray(it.serial_codes) && it.serial_codes.length > 0)
            payload.serial_codes = it.serial_codes;
          if (it.serial_code) payload.serial_codes = [it.serial_code];
          if (it.batch_code) payload.batch_code = it.batch_code;
          if (it.expiry_date) payload.expiry_date = it.expiry_date;
          return payload;
        });
        const headers: Record<string, string> = {};
        headers['X-Idempotency-Key'] = body.idempotencyKey || generateIdempotencyKey('rcpt');
        return {
          url: '/v1/purchase-receipts',
          method: 'POST',
          body: {
            supplier_id: body.supplier_id,
            branch_id: body.branch_id,
            purchase_order_id: body.purchase_order_id ?? undefined,
            reference: body.reference || undefined,
            received_at: body.received_at,
            items,
          },
          headers,
        };
      },
      transformErrorResponse: handlerErrorResponse('Purchases.CreateReceipt'),
      invalidatesTags: ['Receipt', 'PO'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar la recepción',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Purchases.CreateReceipt'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'purchases.receipts.create',
              endpointName: e?.endpointName || 'Purchases.CreateReceipt',
              validationErrors:
                (e as unknown as { details?: unknown })?.details ??
                (e as unknown as { originalError?: { errors?: unknown } })?.originalError?.errors ??
                {},
            }),
          );
        }
      },
    }),

    /* ---------- GET /purchase-orders/{id} ---------- */
    getPODetail: builder.query<ApiResponseBase<PurchaseOrderDetail>, number>({
      query: (id) => ({ url: `/v1/purchase-orders/${id}`, method: 'GET' }),
      transformResponse: (respUnknown: unknown): ApiResponseBase<PurchaseOrderDetail> => {
        const resp = isRecord(respUnknown)
          ? (respUnknown as RawApiResponse<Record<string, unknown>>)
          : {};
        const data = isRecord(resp.data) ? resp.data : {};
        const itemsRaw = asArray(data.items);
        const items: PurchaseOrderLineDetail[] = itemsRaw.map((it) => {
          const row = isRecord(it) ? it : {};
          return {
            inventory_id: row.inventory_id != null ? getNum(row.inventory_id, 0) : undefined,
            sku: getStr(row.sku, ''),
            name: typeof row.name === 'string' ? row.name : undefined,
            ordered_qty: getNum(row.ordered_qty ?? row.qty, 0),
            received_qty: getNum(row.received_qty, 0),
            pending_qty: getNum(row.pending_qty, 0),
            unit_cost: getNum(row.unit_cost, 0),
            track_serial: Boolean(row.track_serial),
            track_batch: Boolean(row.track_batch),
            track_expiry: Boolean(row.track_expiry),
          };
        });
        return {
          success: Boolean(resp.success),
          message: getStr(resp.message, ''),
          data: {
            po_id: getNum(data.id ?? data.po_id, 0),
            status: typeof data.status === 'string' ? data.status : undefined,
            supplier_id: data.supplier_id != null ? getNum(data.supplier_id, 0) : undefined,
            supplier_name: typeof data.supplier_name === 'string' ? data.supplier_name : undefined,
            branch_id: data.branch_id != null ? getNum(data.branch_id, 0) : undefined,
            branch_name: typeof data.branch_name === 'string' ? data.branch_name : undefined,
            product_count: data.product_count != null ? getNum(data.product_count, 0) : null,
            received_qty_total:
              data.received_qty_total != null ? getNum(data.received_qty_total, 0) : null,
            pending_qty_total:
              data.pending_qty_total != null ? getNum(data.pending_qty_total, 0) : null,
            pending_lines: data.pending_lines != null ? getNum(data.pending_lines, 0) : null,
            ordered_at: typeof data.ordered_at === 'string' ? data.ordered_at : null,
            created_at: typeof data.created_at === 'string' ? data.created_at : null,
            items,
          },
        };
      },
      providesTags: (_result, _error, id) => [{ type: 'PO', id }],
      transformErrorResponse: handlerErrorResponse('Purchases.GetPODetail'),
    }),

    closePO: builder.mutation<ApiResponseBase<POStatusMutationResult>, number | string>({
      query: (id) => ({ url: `/v1/purchase-orders/${id}/close`, method: 'POST' }),
      transformErrorResponse: handlerErrorResponse('Purchases.ClosePO'),
      invalidatesTags: ['PO', 'Receipt'],
    }),

    cancelPO: builder.mutation<ApiResponseBase<POStatusMutationResult>, number | string>({
      query: (id) => ({ url: `/v1/purchase-orders/${id}/cancel`, method: 'POST' }),
      transformErrorResponse: handlerErrorResponse('Purchases.CancelPO'),
      invalidatesTags: ['PO', 'Receipt'],
    }),

    /* ---------- GET /purchase-receipts/{id} ---------- */
    getReceiptDetail: builder.query<ApiResponseBase<ReceiptDetail>, number | string>({
      query: (id) => ({ url: `/v1/purchase-receipts/${id}`, method: 'GET' }),
      transformResponse: (respUnknown: unknown): ApiResponseBase<ReceiptDetail> => {
        const resp = isRecord(respUnknown)
          ? (respUnknown as RawApiResponse<Record<string, unknown>>)
          : {};
        const data = isRecord(resp.data) ? resp.data : {};

        const itemsRaw = asArray(data.items);

        const items: ReceiptDetailItem[] = itemsRaw.map((it) => {
          const r = isRecord(it) ? it : {};
          const rawSerial =
            r.serial_code ??
            (r as Record<string, unknown>).SerialCode ??
            (r as Record<string, unknown>).serialCode ??
            null;

          let serial: string | null = null;
          if (typeof rawSerial === 'string') {
            serial = rawSerial;
          } else if (isRecord(rawSerial)) {
            if (typeof rawSerial.String === 'string') serial = rawSerial.String as string;
            else if (typeof rawSerial.value === 'string') serial = rawSerial.value as string;
          }

          return {
            id: r.id != null ? getNum(r.id, 0) : null,
            inventory_id: r.inventory_id != null ? getNum(r.inventory_id, 0) : undefined,
            sku: getStr(r.sku ?? (r as Record<string, unknown>).SKU, ''),
            name: typeof r.name === 'string' ? r.name : null,
            qty: getNum(r.qty ?? (r as Record<string, unknown>).Qty, 0),
            unit_cost: getNum(r.unit_cost ?? (r as Record<string, unknown>).UnitCost, 0),
            serial_code: serial,
            serial_codes: Array.isArray(r.serial_codes)
              ? r.serial_codes.map((value) => getStr(value, '')).filter(Boolean)
              : serial
                ? [serial]
                : [],
            batch_code: typeof r.batch_code === 'string' ? r.batch_code : undefined,
            expiry_date: typeof r.expiry_date === 'string' ? r.expiry_date : undefined,
            received_qty: r.received_qty != null ? getNum(r.received_qty, 0) : null,
            pending_qty: r.pending_qty != null ? getNum(r.pending_qty, 0) : null,
            corrected_qty: r.corrected_qty != null ? getNum(r.corrected_qty, 0) : null,
            net_qty: r.net_qty != null ? getNum(r.net_qty, 0) : null,
          };
        });

        const detail: ReceiptDetail = {
          id: getNum(data.id ?? data.receipt_id, 0),
          supplier_id: getNum(data.supplier_id, 0),
          supplier_name: typeof data.supplier_name === 'string' ? data.supplier_name : null,
          supplier_code: typeof data.supplier_code === 'string' ? data.supplier_code : null,
          branch_id: getNum(data.branch_id, 0),
          branch_name: typeof data.branch_name === 'string' ? data.branch_name : null,
          purchase_order_id:
            data.purchase_order_id != null ? getNum(data.purchase_order_id, 0) : null,
          purchase_order_status:
            typeof data.purchase_order_status === 'string' ? data.purchase_order_status : null,
          status: typeof data.status === 'string' ? data.status : null,
          document_status: typeof data.document_status === 'string' ? data.document_status : null,
          total_qty: data.total_qty != null ? getNum(data.total_qty, 0) : null,
          corrected_qty: data.corrected_qty != null ? getNum(data.corrected_qty, 0) : null,
          net_total_qty: data.net_total_qty != null ? getNum(data.net_total_qty, 0) : null,
          correction_count: data.correction_count != null ? getNum(data.correction_count, 0) : null,
          total_lines: data.total_lines != null ? getNum(data.total_lines, 0) : null,
          idempotency_key: typeof data.idempotency_key === 'string' ? data.idempotency_key : null,
          created_by: data.created_by != null ? getNum(data.created_by, 0) : null,
          reference: (typeof data.reference === 'string' ? data.reference : null) as string | null,
          received_at: getStr(data.received_at, ''),
          created_at: typeof data.created_at === 'string' ? data.created_at : null,
          items,
        };

        return {
          success: Boolean(resp.success),
          message: getStr(resp.message, ''),
          data: detail,
        };
      },
      transformErrorResponse: handlerErrorResponse('Purchases.GetReceiptDetail'),
      providesTags: (_result, _error, id) => [{ type: 'Receipt', id }],
    }),

    correctReceipt: builder.mutation<ApiResponseBase<ReceiptCorrection>, CorrectReceiptInput>({
      query: ({ id, idempotencyKey, ...body }) => ({
        url: `/v1/purchase-receipts/${id}/correct`,
        method: 'POST',
        body,
        headers: {
          'X-Idempotency-Key': idempotencyKey || generateIdempotencyKey(`rcpt-correct-${id}`),
        },
      }),
      transformErrorResponse: handlerErrorResponse('Purchases.CorrectReceipt'),
      invalidatesTags: ['Receipt'],
    }),

    reverseReceipt: builder.mutation<ApiResponseBase<ReceiptCorrection>, ReverseReceiptInput>({
      query: ({ id, idempotencyKey, ...body }) => ({
        url: `/v1/purchase-receipts/${id}/reverse`,
        method: 'POST',
        body,
        headers: {
          'X-Idempotency-Key': idempotencyKey || generateIdempotencyKey(`rcpt-reverse-${id}`),
        },
      }),
      transformErrorResponse: handlerErrorResponse('Purchases.ReverseReceipt'),
      invalidatesTags: ['Receipt'],
    }),

    /* ---------- PRINTS ---------- */
    bulkPrintReceipts: builder.mutation<Blob, BulkReceiptPrintInput>({
      query: (body) => ({
        url: '/v1/purchase-receipts/bulk-print',
        method: 'POST',
        body,
        responseHandler: (response: Response) => response.blob(),
      }),
      transformErrorResponse: handlerErrorResponse('Purchases.Receipts.BulkPrint'),
    }),

    printPOConsolidated: builder.mutation<
      Blob,
      { id: number | string; body: POConsolidatedPrintInput }
    >({
      query: ({ id, body }) => ({
        url: `/v1/purchase-orders/${id}/print`,
        method: 'POST',
        body,
        responseHandler: (response: Response) => response.blob(),
      }),
      transformErrorResponse: handlerErrorResponse('Purchases.PO.PrintConsolidated'),
    }),

    printPOLabels: builder.mutation<Blob, { id: number | string; body: POLabelPrintInput }>({
      query: ({ id, body }) => ({
        url: `/v1/purchase-orders/${id}/labels`,
        method: 'POST',
        body,
        responseHandler: (response: Response) => response.blob(),
      }),
      transformErrorResponse: handlerErrorResponse('Purchases.PO.Labels'),
    }),

    printDocs: builder.mutation<PrintResult, PrintRequestPayload>({
      query: (body) => ({
        url: '/v1/print',
        method: 'POST',
        body,
        responseHandler: async (response: Response) => {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/pdf')) {
            const blob = await response.blob();
            return {
              blob,
              contentType,
              filename: response.headers.get('content-disposition'),
            };
          }
          return response.json();
        },
      }),
      transformErrorResponse: handlerErrorResponse('Purchases.PrintDocs'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              open: true,
              message: e?.message || 'Error al imprimir',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Purchases.PrintDocs'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'purchases.print',
              endpointName: e?.endpointName || 'Purchases.PrintDocs',
              validationErrors:
                (e as unknown as { details?: unknown })?.details ??
                (e as unknown as { originalError?: { errors?: unknown } })?.originalError?.errors ??
                {},
            }),
          );
        }
      },
    }),
  }),
});

export const {
  useCreatePOMutation,
  useCreateReceiptMutation,
  useListPOsQuery,
  useLazyListPOsQuery,
  useListReceiptsQuery,
  useLazyListReceiptsQuery,
  useLazyGetPODetailQuery,
  useClosePOMutation,
  useCancelPOMutation,
  useLazyGetReceiptDetailQuery,
  useCorrectReceiptMutation,
  useReverseReceiptMutation,
  useBulkPrintReceiptsMutation,
  usePrintPOConsolidatedMutation,
  usePrintPOLabelsMutation,
  usePrintDocsMutation,
} = purchasesApi;
